import React, { memo, useState, useEffect, useMemo } from 'react';
import {
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  MenuItem,
  IconButton,
  Icon
} from 'investira.react.components';
import { dates, formats } from 'investira.sdk';
import { useEmpresasContext } from '../../../../../contexts/EmpresasContext';
import { HighchartsReact } from 'investira.react.charts';
import { validators } from 'investira.sdk';
import services from '../../../../../services';
const EmpresaCharts = memo(props => {
  const { state, actions } = useEmpresasContext();
  const [, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const variants = useMemo(() => {
    const TODAY = new Date();
    const DATA_FIM = dates.toSqlDate(dates.toSqlDate(TODAY));
    const DATA_INICIO = dates.addYears(dates.toSqlDate(TODAY), -10);
    const xCsLastCategoryIndex = Math.max((data?.categories?.length || 1) - 1, 0);
    const xCsEdgePadding = Math.max(xCsLastCategoryIndex * 0.015, 0.015);
    const xCsParams = {
      data_inicio: DATA_INICIO,
      data_fim: DATA_FIM,
      pessoa_id: state.pessoa.pessoa_id,
      sort: 'data ASC'
    };

    // Formata a resposta compartilhada pelos gráficos de capital social e quantidade de papéis.
    const pvFormatCsData = pData => {
      const xData = {
        categories: [],
        cs: [],
        on: [],
        pn: [],
        totalPapeis: []
      };
      pData.forEach(xPoint => {
        const xDate = dates.toDate(xPoint.data).getTime();
        const xPapeis = xPoint?.acao ? Object.keys(xPoint.acao) : [];
        const xCapitalSocial = xPoint?.cs;
        const xOn = xPapeis.includes('on') ? xPoint.acao.on : null;
        const xPn = xPapeis.includes('pn') ? xPoint.acao.pn : null;

        // Ignora o período completo quando não há dados representados nos gráficos.
        if (validators.isNull(xCapitalSocial) && validators.isNull(xOn) && validators.isNull(xPn)) {
          return;
        }

        xData.categories.push(formats.formatDateCustom(xDate, 'DD/MMM/YYYY'));
        xData.cs.push(xCapitalSocial);
        xData.on.push(xOn);
        xData.pn.push(xPn);
        xData.totalPapeis.push(
          validators.isNull(xOn) && validators.isNull(xPn)
            ? null
            : {
                y: (xOn || 0) + (xPn || 0),
                on: xOn,
                pn: xPn
              }
        );
      });
      return xData;
    };
    const xResult = {
      pl: {
        key: 'pl',
        request: services.pessoas.pl,
        params: {
          data_inicio: DATA_INICIO,
          data_fim: DATA_FIM,
          pessoa_id: state.pessoa.pessoa_id,
          sort: 'data ASC',
          consolidacao_tipo: state.config.consolidacao_tipo
        },
        formatData: pData => {
          return pData.map(xPoint => {
            const xDate = dates.startOf('month', dates.toDate(xPoint.data)).getTime();
            return [xDate, xPoint.pl];
          });
        },
        options: {
          title: {
            text:
              state.pessoa?.setor_id === '65.41.3.00' ? 'Patrimônio Social' : 'Patrimônio líquido'
          },
          tooltip: {
            formatter: function (tooltip) {
              const xDate = formats.formatDateCustom(this.point.x, 'MMM/YYYY');
              const xValue = formats.friendlyNumber(this.point.y, 2, false);
              const xFullValue = formats.formatNumber(this.point.y, 2, true, false);
              return `
              <span style="font-size: 1em"><b>${xDate}</b></span><br/>
              <span style="color:${this.color}">\u25CF</span>
              ${this.series.name}: <b>${xValue}</b> (${xFullValue})<br/>
              `;
            }
          },
          yAxis: {
            type: 'linear',
            title: {
              text:
                state.pessoa?.setor_id === '65.41.3.00'
                  ? `Patrimônio social (R$)`
                  : `Patrimônio líquido (R$)`
            }
          },
          xAxis: {
            type: 'datetime',
            crosshair: true
          },
          series: [
            {
              name: state.pessoa?.setor_id === '65.41.3.00' ? 'PS' : 'PL',
              type: 'line',
              data: Array.isArray(data) ? data : [],
              zones: [
                {
                  value: 0,
                  // Up to zero
                  color: '#ed442c' // Color for values above zero
                },
                {
                  color: '#00dfa8' // Color for values below zero
                }
              ]
            }
          ]
        }
      },
      saldo: {
        key: 'saldo',
        request: services.pessoas.saldos,
        params: {
          data_inicio: DATA_INICIO,
          data_fim: DATA_FIM,
          pessoa_id: state.pessoa.pessoa_id,
          sort: 'data ASC',
          consolidacao_tipo: state.config.consolidacao_tipo,
          conta: '3.08.00.00.00.00.00'
        },
        formatData: pData => {
          return pData.map(xPoint => {
            const xDate = dates.startOf('month', dates.toDate(xPoint.data)).getTime();
            return [xDate, xPoint.saldo * -1];
          });
        },
        options: {
          title: {
            text: 'Superávit/Déficit Técnico'
          },
          tooltip: {
            formatter: function (tooltip) {
              const xDate = formats.formatDateCustom(this.point.x, 'MMM/YYYY');
              const xValue = formats.friendlyNumber(this.point.y, 2, false);
              const xFullValue = formats.formatNumber(this.point.y, 2, true, false);
              const xSerieName = this.point.y > 0 ? 'Superávit' : 'Déficit';
              return `
              <span style="font-size: 1em"><b>${xDate}</b></span><br/>
              <span style="color:${this.color}">\u25CF</span>
              ${xSerieName}: <b>${xValue}</b> (${xFullValue})<br/>
              `;
            }
          },
          yAxis: {
            type: 'linear',
            title: {
              text: 'Superávit/Déficit Técnico'
            }
          },
          xAxis: {
            type: 'datetime',
            crosshair: true
          },
          series: [
            {
              name: 'Superávit/Déficit',
              type: 'line',
              data: Array.isArray(data) ? data : [],
              zones: [
                {
                  value: 0,
                  // Up to zero
                  color: '#ed442c' // Color for values above zero
                },
                {
                  color: '#00dfa8' // Color for values below zero
                }
              ]
            }
          ]
        }
      },
      cs: {
        key: 'cs',
        title: 'Capital social',
        request: services.pessoas.cs,
        params: xCsParams,
        formatData: pvFormatCsData,
        options: {
          title: {
            text: 'Capital social'
          },
          chart: {
            type: 'line'
          },
          tooltip: {
            formatter: function () {
              const xDate = this.point.category;
              const xValue = formats.friendlyNumber(this.point.y, 2, false);
              const xFullValue = formats.formatNumber(this.point.y, 2, true, false);

              return `<span style="font-size: 1em"><b>${xDate}</b></span><br/><span style="color:${this.color}">●</span>${this.series.name}: <b>${xValue}</b> (${xFullValue})<br/>`;
            }
          },
          yAxis: {
            type: 'linear',
            title: {
              text: 'Capital social (R$)'
            },
            labels: {
              formatter: function () {
                return formats.friendlyNumber(this.value, 2, true);
              }
            },
            showEmpty: false
          },
          xAxis: {
            categories: data?.categories || [],
            crosshair: {
              width: 1
            },
            min: -xCsEdgePadding,
            max: xCsLastCategoryIndex + xCsEdgePadding,
            tickmarkPlacement: 'on',
            tickPositions: (data?.categories || []).map((_pCategory, pIndex) => pIndex)
          },
          series: [
            {
              name: 'Capital social',
              type: 'line',
              data: data?.cs || [],
              pointPlacement: 'on',
              connectNulls: true,
              marker: {
                enabled: true,
                radius: 3
              }
            }
          ]
        }
      },
      papeis: {
        key: 'papeis',
        title: 'Quantidade de papéis',
        request: services.pessoas.cs,
        params: xCsParams,
        formatData: pvFormatCsData,
        options: {
          title: {
            text: 'Quantidade de papéis'
          },
          chart: {
            type: 'line'
          },
          tooltip: {
            outside: true,
            distance: 12,
            style: {
              zIndex: 1500
            },
            formatter: function () {
              const xDate = this.point.category;
              let xHTML = `<span style="font-size: 1em"><b>${xDate}</b></span><br/>`;
              const xPoint = this.point;

              if (!validators.isNull(xPoint.on)) {
                xHTML += `<span style="color:#f1b40f">●</span>ON: <b>${formats.friendlyNumber(
                  xPoint.on,
                  2,
                  false
                )}</b> (${formats.formatNumber(xPoint.on, 0, true, false)})<br/>`;
              }

              if (!validators.isNull(xPoint.pn)) {
                xHTML += `<span style="color:#058DC7">●</span>PN: <b>${formats.friendlyNumber(
                  xPoint.pn,
                  2,
                  false
                )}</b> (${formats.formatNumber(xPoint.pn, 0, true, false)})<br/>`;
              }

              xHTML += `Total de Papéis: <b>${formats.friendlyNumber(
                xPoint.y,
                2,
                false
              )}</b> (${formats.formatNumber(xPoint.y, 0, true, false)})<br/>`;
              return xHTML;
            }
          },
          yAxis: {
            type: 'linear',
            title: {
              text: 'Total de Papéis'
            },
            labels: {
              formatter: function () {
                return formats.friendlyNumber(this.value, 2, false);
              }
            },
            showEmpty: false
          },
          xAxis: {
            categories: data?.categories || [],
            crosshair: {
              width: 1
            },
            min: -xCsEdgePadding,
            max: xCsLastCategoryIndex + xCsEdgePadding,
            tickmarkPlacement: 'on',
            tickPositions: (data?.categories || []).map((_pCategory, pIndex) => pIndex)
          },
          plotOptions: {
            series: {
              inactiveOtherPoints: true,
              states: {
                inactive: {
                  opacity: 0.2
                }
              }
            }
          },
          series: [
            {
              name: 'Total de Papéis',
              type: 'line',
              data: data?.totalPapeis || [],
              pointPlacement: 'on',
              opacity: 1,
              marker: {
                enabled: true,
                radius: 3
              }
            }
          ]
        }
      }
    };
    return xResult;
  }, [data, state.pessoa.pessoa_id, state.config.consolidacao_tipo]);
  function handleClose() {
    actions.handleChartVariantChange(null);
    setData([]);
  }
  const handleMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  useEffect(() => {
    if (!validators.isNull(state.variantChart)) {
      setLoading(true);
      const xVariant = variants[state.variantChart];
      xVariant.request(
        {
          ...xVariant.params,
          data: null
        },
        rRes => {
          const xData = xVariant.formatData(rRes.data);
          console.log('xData', xData);
          setData(xData);
          setLoading(false);
        }
      );
    }
  }, [state.variantChart]);

  // Render

  if (validators.isNull(state.variantChart)) {
    return null;
  }
  const options = {
    ...variants[state.variantChart].options
  };
  function renderMenu(pVariants, pSetorId) {
    const xVariants = {
      ...pVariants
    };
    if (pSetorId === '65.41.3.00') {
      delete xVariants.cs;
      delete xVariants.papeis;
    } else {
      delete xVariants.saldo;
    }
    return (
      <Menu
        anchorEl={anchorEl}
        id={`chart-menu`}
        open={open}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          component: 'nav',
          elevation: 1,
          sx: {
            overflow: 'visible',
            mt: 1.5
          }
        }}
        transformOrigin={{
          horizontal: 'center',
          vertical: 'top'
        }}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom'
        }}
        keepMounted>
        {Object.values(xVariants).map(xItem => {
          return (
            <MenuItem key={xItem.key} onClick={() => actions.handleChartVariantChange(xItem.key)}>
              {xItem.title || xItem.options.title.text}
            </MenuItem>
          );
        })}
      </Menu>
    );
  }
  return (
    <Dialog open={Boolean(state.variantChart)} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          m: 0,
          p: 2
        }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Stack>
            <IconButton onClick={handleMenuClick} color="primary">
              <Icon iconName="menu" size={24} />
            </IconButton>
          </Stack>
          <Stack>Histórico</Stack>
        </Stack>
        {renderMenu(variants, state.pessoa.setor_id)}
      </DialogTitle>
      <IconButton
        onClick={handleClose}
        color="primary"
        sx={{
          position: 'absolute',
          right: 8,
          top: 16
        }}>
        <Icon iconName="cancel" size={16} />
      </IconButton>
      <DialogContent
        sx={{
          paddingTop: '8px !important'
        }}>
        <HighchartsReact key={state.variantChart} options={options} />
      </DialogContent>
    </Dialog>
  );
});
EmpresaCharts.propTypes = {};
export default EmpresaCharts;
